import { useState } from "react";
import { useAnalyticsQuery } from "@/hooks/useAnalyticsQuery";
import { analyticsApi } from "@/services/apiClient";
import { daysAgoISODate, todayISODate } from "@/stores/filterStore";
import SectionRangePicker from "@/components/SectionRangePicker";
import KpiCard, { KpiRow } from "@/components/KpiCard";
import InfoTooltip from "@/components/InfoTooltip";
import { fmt, toolLabel } from "@/lib/format";
import Panel from "./Panel";
import Pagination from "@/components/Pagination";
import Skeleton from "./Skeleton";

// 사내 구성원 사용 현황 (2026-07-20) — 전체 사용자 탭의 '사내 구성원' 세그먼트.
// 내부 무료 정책(차감 0·원가만 기록)이라 잔액·충전·조정 개념이 없다 — 관리 질문은
// "누가·얼마나 쓰나(=사내 도입 비용)". 기간 내 사용 실적자만 표시(로스터 312명
// 전체가 아니라) — 미사용자는 정보가치가 없고 목록만 흐린다.
function defaultRange() {
  return { from: daysAgoISODate(29), to: todayISODate() };
}

export default function InternalUsersView() {
  const [range, setRange] = useState(defaultRange);
  const [search, setSearch] = useState("");
  const [activeOnly, setActiveOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const q = useAnalyticsQuery(
    (p, s) => analyticsApi.internalUsers({ from: range.from, to: range.to }, s),
    [range.from, range.to], range);

  if (q.loading && !q.data) return <Skeleton />;
  if (q.error) return <Panel title="사내 구성원"><p className="text-sm text-red-600">불러오기 실패 — {String(q.error.message || q.error)}</p></Panel>;
  const d = q.data || {};
  const users = (d.users || [])
    .filter((u) => !activeOnly || u.requests > 0)
    .filter((u) => !search || (u.email || u.userId).toLowerCase().includes(search.toLowerCase()));
  const safePage = Math.min(page, Math.max(1, Math.ceil(users.length / pageSize)));
  const pageUsers = users.slice((safePage - 1) * pageSize, (safePage - 1) * pageSize + pageSize);

  return (
    <div className="space-y-4">
      <KpiRow cols={4}>
        <KpiCard label="전체 구성원" value={`${fmt.int(d.totals?.rosterCount ?? 0)}명`} tone="brand" hero
                 sublabel="사내 CMS 풀 계정 전체" />
        <KpiCard label="이 기간 사용자" value={`${fmt.int(d.totals?.activeCount ?? 0)}명`} tone="good"
                 sublabel={`온보딩률 ${d.totals?.rosterCount ? Math.round((d.totals.activeCount / d.totals.rosterCount) * 100) : 0}% — 도입 확산의 핵심 지표`} />
        <KpiCard label="요청 수" value={fmt.int(d.totals?.requests ?? 0)}
                 sublabel="이 기간 사내 호출 합계" />
        <KpiCard label="사내 사용 원가" value={fmt.krw(d.totals?.costKrw ?? 0)} tone="warn"
                 sublabel="무과금 — 회사가 부담하는 AI 원가" />
      </KpiRow>

      <Panel
        title="사내 구성원 사용 내역"
        action={
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1.5 text-[12.5px] text-ink-500">
              <input type="checkbox" checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)}
                     className="h-3.5 w-3.5 accent-brand-600" />
              사용자만
            </label>
            <input value={search} onChange={(e) => setSearch(e.target.value)}
                   placeholder="이메일 검색"
                   className="w-40 rounded-lg border border-ink-200 px-2.5 py-1.5 text-[12.5px] focus:border-brand-400 focus:outline-none" />
            <SectionRangePicker from={range.from} to={range.to} onChange={(from, to) => setRange({ from, to })} />
          </div>
        }>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[560px] text-[13px]">
            <thead>
              <tr className="text-left text-ink-400">
                <th className="pb-2 font-medium">구성원</th>
                <th className="pb-2 text-right font-medium">계정 생성</th>
                <th className="pb-2 font-medium">주 사용 도구</th>
                <th className="pb-2 text-right font-medium">요청</th>
                <th className="pb-2 text-right font-medium">사용 원가</th>
                <th className="pb-2 text-right font-medium">최근 사용 (KST)</th>
              </tr>
            </thead>
            <tbody>
              {pageUsers.map((u) => (
                <tr key={u.userId} className="border-t border-ink-100">
                  <td className={`py-2 ${u.requests ? "" : "text-ink-400"}`}>
                    {u.email || <span className="font-mono text-[12px] text-ink-400">{u.userId.slice(0, 18)}…</span>}
                    {!u.enabled && <span className="ml-1.5 rounded bg-ink-100 px-1.5 py-0.5 text-[10.5px] text-ink-500">비활성</span>}
                  </td>
                  <td className="py-2 text-right tabular-nums text-ink-400">{u.joinedAt || "—"}</td>
                  <td className="py-2 text-ink-500">{u.topTool ? toolLabel(u.topTool) : "—"}</td>
                  <td className="py-2 text-right tabular-nums">{u.requests ? fmt.int(u.requests) : <span className="text-ink-300">0</span>}</td>
                  <td className="py-2 text-right tabular-nums">{u.costKrw ? fmt.krw(u.costKrw) : <span className="text-ink-300">—</span>}</td>
                  <td className="py-2 text-right tabular-nums text-ink-400">{fmt.kstDateTime(u.lastSeen)?.slice(0, 16) || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination total={users.length} page={safePage} onPageChange={setPage} pageSize={pageSize} onPageSizeChange={setPageSize} />
          {!users.length && (
            <p className="py-6 text-center text-sm text-ink-400">
              조건에 맞는 구성원이 없습니다.
            </p>
          )}
        </div>
        <p className="mt-2 text-[12px] text-ink-400">
          무과금 정책이라 잔액·충전 열이 없습니다. 원가는 공시단가 기반 산출 — FinOps 정책·규칙 참조.
        </p>
      </Panel>
    </div>
  );
}
